import { Intercom } from "@intercom/messenger-js-sdk";
import { register as registerArchives } from "@sg-protect/archives";
import { reloadPage } from "@sg-protect/core";
import { register as registerHome } from "@sg-protect/home";
import { type Environment, FeatureFlagKeys, createFeatureFlagClient } from "@sg-protect/federation-runtime";
import { createI18nextPlugin } from "@sg-protect/federation-runtime/i18n";
import { getFeatureFlag, initializeFirefly } from "@sg-protect/federation-runtime/squide";
import { register as registerMigrate } from "@sg-protect/migrate";
import { register as registerMove } from "@sg-protect/move";
import { registerDelegatedReviews } from "@sg-protect/reviews";
import config from "@sg-protect/runtime-config";
import { registerSharedReports } from "@sg-protect/shared-reports";
import {
    AppRoot,
    type DeferredRegistrationData,
    type ProtectRuntimeContext,
    identity,
    initSessionReplay,
    runPageLoadSteps
} from "@sg-protect/shell";
import { registerShell } from "@sg-protect/shell/register";
import { register as registerTenantAssessment } from "@sg-protect/tenant-assessment";
import { BrowserConsoleLogger, LogLevel, type RootLogger } from "@workleap/logging";
import { type InitializeTelemetryOptions, LogRocketLogger, initializeTelemetry } from "@workleap/telemetry/react";
import { createRoot } from "react-dom/client";
import { MaintenancePage } from "./MaintenancePage.tsx";
import { registerHost } from "./registerHost.ts";

import "@sg-protect/shell/styles.css";

runPageLoadSteps();

const env = process.env.APP_ENV as Environment;
const isMsw = env === "msw";
const isLocalDevelopment = env === "local" || isMsw;
const isDevelopment = env === "development" || isLocalDevelopment;

const RootHostnames = ["sharegate.com", "sharegate-stg.com", "sharegate-dev.com"];

function getRootHostname(): string | undefined {
    const hostname = document.location.hostname;

    return RootHostnames.find(rootHostname => hostname === rootHostname || hostname.endsWith(`.${rootHostname}`));
}

const launchDarklyClient = createFeatureFlagClient(env, config.get().launch_darkly.client_id, identity.ldContext);

try {
    // Always initialize the client before creating the plugin instance.
    await launchDarklyClient.waitForInitialization(5);
} catch (error: unknown) {
    console.error("[host-app] LaunchDarkly client failed to initialize:", error);
}

launchDarklyClient.on(`change:${FeatureFlagKeys.MaintenancePageEnabled}`, () => {
    reloadPage();
});

const root = createRoot(document.getElementById("root")!);

if (getFeatureFlag(launchDarklyClient, FeatureFlagKeys.MaintenancePageEnabled, false)) {
    root.render(<MaintenancePage />);
} else {
    const isLogRocketEnabled =
        getFeatureFlag(launchDarklyClient, FeatureFlagKeys.UseLogRocket, false) && config.get().logrocket?.app_id;
    const isHoneycombEnabled =
        getFeatureFlag(launchDarklyClient, FeatureFlagKeys.UseHoneycomb, false) && config.get().honeycomb?.proxy;
    const sessionReplayConfig = getFeatureFlag(
        launchDarklyClient,
        FeatureFlagKeys.MixpanelSessionRecordingEnabled,
        false
    )
        ? config.get().mixpanel_session_replay
        : undefined;

    const consoleLogger = new BrowserConsoleLogger({ logLevel: LogLevel.information });
    const loggers: RootLogger[] = [consoleLogger];

    if (isLogRocketEnabled) {
        loggers.push(new LogRocketLogger({ logLevel: LogLevel.information }));
    }

    const telemetryOptions: InitializeTelemetryOptions = {
        loggers,
        mixpanel: {
            envOrTrackingApiBaseUrl: config.get().insights.api,
            options: {
                trackingEndpoint: "/bff/telemetry/track-single-event"
            }
        }
    };

    if (isLogRocketEnabled) {
        telemetryOptions.logRocket = {
            appId: config.get().logrocket!.app_id!,
            options: {
                rootHostname: getRootHostname(),
                transformers: [
                    options => ({ ...options, dom: { ...options.dom, hideSelectors: ["[data-replay-hidden]"] } })
                ]
            }
        };
    }

    if (isHoneycombEnabled) {
        telemetryOptions.honeycomb = {
            namespace: "protect",
            serviceName: "sgp-frontend",
            apiServiceUrls: [/^(?:https:\/\/)(?:[a-zA-Z0-9-]+\.){0,2}sharegate(-stg|-dev)?.com\/[^.]*/],
            options: {
                proxy: config.get().honeycomb!.proxy,
                documentLoadInstrumentation: false,
                fetchInstrumentation: defaults => ({
                    ...defaults,
                    applyCustomAttributesOnSpan: (span, request) => {
                        if (request instanceof Request) {
                            span.setAttribute("name", `${request.method} ${new URL(request.url).pathname}`);
                        }
                    },
                    ignoreUrls: [/\/bff\/telemetry\/track-single-event/]
                }),
                transformers: [
                    options => {
                        return {
                            ...options,
                            resourceAttributes: {
                                ...options.resourceAttributes,
                                // We add this property on all honeycomb events, so the collector knows in which
                                // project to send the events
                                "cloud.region": config.region
                            }
                        };
                    }
                ]
            }
        };
    }

    const telemetryClient = initializeTelemetry("sg", telemetryOptions);

    if (sessionReplayConfig) {
        initSessionReplay(
            { apiHost: sessionReplayConfig.proxy, libBasePath: sessionReplayConfig.lib_base_path },
            consoleLogger
        );
    }

    const runtime = initializeFirefly<ProtectRuntimeContext, DeferredRegistrationData>({
        mode: isDevelopment ? "development" : "production",
        useMsw: isMsw,
        context: {
            environment: env
        },
        localModules: [
            registerHost,
            registerShell,
            registerTenantAssessment,
            registerMigrate,
            registerMove,
            registerArchives,
            registerDelegatedReviews,
            registerSharedReports,
            registerHome
        ],
        startMsw: async x => {
            // Files that include an import to the "msw" package are included dynamically to prevent adding
            // unused MSW stuff to the code bundles.
            return (await import("./startMsw.ts")).startMsw(x.requestHandlers);
        },
        loggers,
        launchDarklyClient,
        plugins: [createI18nextPlugin]
    });

    // The Playwright smoke test reads the route tree from here. Compared inline, not through `isMsw`, so the bundler
    // drops the import from non-MSW builds.
    if (process.env.APP_ENV === "msw") {
        void import("./exposeRoutes.ts").then(x => x.exposeRoutes(runtime));
    }

    if (config.get().intercom?.app_id) {
        Intercom({
            app_id: config.get().intercom!.app_id!,
            created_at: Math.floor(Date.now() / 1000)
        });
    }

    root.render(<AppRoot telemetryClient={telemetryClient} runtime={runtime} />);
}
